import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
//import ExecutionHistory from "../componentsExecutionHistory";
//import SettingsPage from "../componentsSettingsPage";
import { useBreadcrumb } from "./../hooks/useBreadcrumb";
import useProjectStore, { runProjectId } from "../store/useProjectStore";

/**
 * "3 of 5 analyses left · access ends 2 Oct" for a guest.
 *
 * Derived from the agent list the server already sent rather than a second
 * request: every unlocked-or-used card carries its own runsUsed/runsLimit, so
 * the strip cannot disagree with the cards underneath it.
 *
 * Renders nothing for everyone else, because nothing else ever carries these
 * fields.
 */
function GuestAllowanceBanner({ agents }) {
  const scoped = (agents || []).filter(
    (a) => typeof a.runsLimit === "number" && a.runsLimit > 0,
  );
  if (!scoped.length) return null;

  const total = scoped.reduce((n, a) => n + a.runsLimit, 0);
  const used = scoped.reduce(
    (n, a) => n + Math.min(a.runsUsed || 0, a.runsLimit),
    0,
  );
  const left = Math.max(0, total - used);

  const expiry = readGuestExpiry();
  const spent = left === 0;

  // Colours come from the light palette the agent cards use (#E7E4E0 borders,
  // white surface, #E57600 accent) rather than the --text-*/--border variables,
  // which still hold dark-theme values: --border is a 7% WHITE line and
  // --text-primary is #f8fafc, both effectively invisible on this background.
  return (
    <div
      className="d-flex align-items-center gap__12 mb__16"
      style={{
        flexWrap: "wrap",
        padding: "12px 16px",
        borderRadius: "12px",
        border: `1px solid ${spent ? "#E7E4E0" : "rgba(229,118,0,0.28)"}`,
        background: spent ? "var(--color-ffffff)" : "var(--orange-bg)",
      }}
    >
      <span
        className={`fb__10 fw__800 ${spent ? "fc__5F636A" : "fc__E57600"}`}
        style={{
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        Guest access
      </span>

      <span className="fb__13 fc__5F636A">
        {spent ? (
          <>
            All {total} analyses used. Your reports stay available in History.
          </>
        ) : (
          <>
            <strong className="fw__700 fc__0E1218">
              {left} of {total}
            </strong>{" "}
            analyses left.
          </>
        )}
        {expiry && (
          <>
            {" · "}
            Access ends <strong className="fw__600 fc__0E1218">{expiry}</strong>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * When the guest's run window closes, formatted for display, or null.
 *
 * Read from the stored user because that is where the sign-in response put it;
 * a bad or missing value simply drops the clause rather than breaking the strip.
 */
function readGuestExpiry() {
  try {
    const raw = localStorage.getItem("user");
    const at = raw ? JSON.parse(raw)?.temporaryAccess?.expiresAt : null;
    if (!at) return null;
    const date = new Date(at);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch {
    return null;
  }
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard({ agents, loading }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [refreshHistory, setRefreshHistory] = useState(0);
  // Which project a launched run belongs to. Chosen in the header
  // (ProjectSwitcher), which also keeps it pointing at a project that still
  // exists — this screen only reads it, and carries it into the agent chat.
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const storeProjects = useProjectStore((s) => s.projects);

  const showHistoryTab = location.pathname === "/history";
  const showSettingsTab = location.pathname === "/settings";

  useBreadcrumb(
    [{ label: "Workspace", to: "/mainScreen" }, { label: "Agent Library" }],
    [location.pathname],
  );

  // const handleOpenExecution = (execution) => {
  //     const from = showHistoryTab ? "&from=history" : "";
  //     navigate(`/agent/${execution.agentId}?executionId=${execution.id}${from}`);
  // };

  // Agents can belong to several categories; older records only carry the
  // single `category` string, so fall back to it.
  const agentCategories = (agent) =>
    agent.categories?.length
      ? agent.categories
      : agent.category
        ? [agent.category]
        : [];

  // Admin-defined sequence wins (Admin → Agents → ↑/↓). Agents that share a
  // position — including everything before an admin has ever reordered — keep
  // the original category-then-name ordering.
  const sortedAgents = [...agents].sort((a, b) => {
    const orderDiff = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    const [ca = ""] = agentCategories(a);
    const [cb = ""] = agentCategories(b);
    if (ca < cb) return -1;
    if (ca > cb) return 1;
    return a.name.localeCompare(b.name);
  });

  // Guests see the whole library with most of it locked, so float what they can
  // actually run to the top and let the locked cards trail. sort() is stable, so
  // the admin ordering above survives inside each group — and `locked` is only
  // ever set for guests, making this a no-op for everyone else.
  const visibleAgents = sortedAgents.sort(
    (a, b) => Number(!!a.locked) - Number(!!b.locked),
  );

  const categories = [
    "All",
    ...Array.from(new Set(visibleAgents.flatMap(agentCategories))),
  ];

  const displayedAgents =
    selectedCategory === "All"
      ? visibleAgents
      : visibleAgents.filter((a) =>
          agentCategories(a).includes(selectedCategory),
        );

  const getInitials = (name) => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const selectedProject =
    storeProjects.find((p) => p.id === selectedProjectId) || null;

  // Carried in the URL so a shared or reloaded link still names the project
  // the run belongs to. "No project" (staff with free URL entry, guests)
  // deliberately sends none.
  const launchAgent = (agentId) => {
    const runIn = runProjectId(selectedProjectId);
    const qs = runIn ? `?projectId=${runIn}` : "";
    navigate(`/agent/${agentId}${qs}`);
  };

  return (
    <section className="wrapper__agent__library wrapper__main">
      <div className="wrapper__container">
        <div className="head mb__24">
          <h2 className="h2 mb__6 fc__0E1218 fw__700">Agent Library</h2>
          <p className="fb__16  fc__5F636A">
            <span className="agentLength">{agents.length}</span> autonomous
            agents across audit, research, strategy, and monitoring.
          </p>
        </div>

        {/* ── Sticky tab strip ─────────────────────────────────────── */}
        {/* <div className="sticky-tab-strip">
                    <div className="tab-strip-row">
                        <div
                            className="tab-strip"
                            style={{
                                backdropFilter: "blur(20px)",
                                WebkitBackdropFilter: "blur(20px)",
                            }}
                        >
                            <button
                                className={`tab-btn${!showHistoryTab && !showSettingsTab ? " tab-active" : ""}`}
                                onClick={() => navigate("/")}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    style={{ flexShrink: 0 }}
                                >
                                    <rect
                                        x="1"
                                        y="1"
                                        width="4.5"
                                        height="4.5"
                                        rx="1.2"
                                        fill="currentColor"
                                        opacity="0.9"
                                    />
                                    <rect
                                        x="8.5"
                                        y="1"
                                        width="4.5"
                                        height="4.5"
                                        rx="1.2"
                                        fill="currentColor"
                                        opacity="0.9"
                                    />
                                    <rect
                                        x="1"
                                        y="8.5"
                                        width="4.5"
                                        height="4.5"
                                        rx="1.2"
                                        fill="currentColor"
                                        opacity="0.9"
                                    />
                                    <rect
                                        x="8.5"
                                        y="8.5"
                                        width="4.5"
                                        height="4.5"
                                        rx="1.2"
                                        fill="currentColor"
                                        opacity="0.9"
                                    />
                                </svg>
                                <span className="tab-label">Available Agents</span>
                                {agents.length > 0 && (
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: "20px",
                                            height: "20px",
                                            padding: "0 5px",
                                            background:
                                                !showHistoryTab && !showSettingsTab
                                                    ? "rgba(255,255,255,0.15)"
                                                    : "rgba(255,255,255,0.06)",
                                            borderRadius: "10px",
                                            fontSize: "10px",
                                            fontWeight: "700",
                                        }}
                                    >
                                        {agents.length}
                                    </span>
                                )}
                            </button>
                            <button
                                className={`tab-btn${showHistoryTab ? " tab-active" : ""}`}
                                onClick={() => {
                                    setRefreshHistory((prev) => prev + 1);
                                    navigate("/history");
                                }}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    style={{ flexShrink: 0 }}
                                >
                                    <circle
                                        cx="7"
                                        cy="7"
                                        r="5.5"
                                        stroke="currentColor"
                                        strokeWidth="1.4"
                                        fill="none"
                                    />
                                    <polyline
                                        points="7,4 7,7.5 9.5,9"
                                        stroke="currentColor"
                                        strokeWidth="1.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="tab-label">Execution History</span>
                            </button>
                        </div>
                    </div>
                </div> */}
        {/* ── End sticky tab strip ──────────────────────────────────── */}

        {/* Skills Tab */}
        {!showHistoryTab && !showSettingsTab && (
          <React.Fragment>
            {/* The picker itself now lives in the header; what stays here is
                the one thing the header cannot say — which domains a run in
                this project is allowed to target. */}
            {!loading && selectedProject?.allowedDomains?.length > 0 && (
              <div
                style={{
                  fontSize: "11px",
                  color: "var(--text-muted)",
                  lineHeight: "1.5",
                  marginBottom: "16px",
                }}
              >
                Agents can target{" "}
                <strong style={{ color: "var(--text-secondary)" }}>
                  {selectedProject.allowedDomains.join(", ")}
                </strong>
              </div>
            )}

            {/* Guest allowance strip — only rendered when the server marked
                some cards locked, i.e. for link-invited users. */}
            <GuestAllowanceBanner agents={visibleAgents} />

            {/* Category filter pills */}
            {!loading && categories.length > 2 && (
              <div className="wrapper__agent__filter mb__24 d-flex align-items-center col__gap__6">
                {categories.map((cat) => {
                  const isActive = selectedCategory === cat;
                  const count =
                    cat === "All"
                      ? visibleAgents.length
                      : visibleAgents.filter((a) =>
                          agentCategories(a).includes(cat),
                        ).length;
                  return (
                    <button
                      className={
                        isActive
                          ? "wrapper__ag__filter active"
                          : "wrapper__ag__filter"
                      }
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                      <span
                        className={
                          isActive ? "filter__value active" : "filter__value"
                        }
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {loading ? (
              <div className="spinner-center">
                <div className="spinner-lg"></div>
                <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                  Loading skills...
                </span>
              </div>
            ) : displayedAgents.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "var(--text-muted)",
                }}
              >
                No agents in this category
              </div>
            ) : (
              <div className="wrapper__agents__row custom__row">
                {displayedAgents.map((agent) => (
                  <div
                    key={agent.id}
                    className="wrapper__agents__col"
                    style={
                      agent.beta || agent.locked
                        ? {
                            opacity: 0.45,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                            filter: "grayscale(0.7)",
                          }
                        : {}
                    }
                  >
                    <div className="wrapper__agent__card">
                      <div className="wrapper__acx__head mb__16 d-flex align-items-center justify-content-between">
                        <div
                          className="agent__icon__wrap"
                          style={{ marginBottom: 0 }}
                        >
                          {agent.icon ? (
                            agent.icon
                          ) : (
                            <span
                              style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "14px",
                                fontWeight: "700",
                                letterSpacing: "-0.02em",
                                background: "var(--gradient-brand)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                userSelect: "none",
                              }}
                            >
                              {getInitials(agent.name)}
                            </span>
                          )}
                        </div>
                        <div className="wrapper__ag__cardCat">
                          {agent.beta && (
                            <span className="agent__beta">Beta</span>
                          )}
                          {agent.locked && (
                            // "Used" and "Locked" are different facts and a
                            // single grey card cannot tell them apart: one is
                            // something they spent, the other was never theirs.
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: "800",
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color:
                                  agent.lockReason === "used"
                                    ? "#22d3ee"
                                    : "var(--text-muted)",
                                padding: "2px 7px",
                                background:
                                  agent.lockReason === "used"
                                    ? "rgba(6,182,212,0.1)"
                                    : "rgba(255,255,255,0.05)",
                                border: `1px solid ${
                                  agent.lockReason === "used"
                                    ? "rgba(6,182,212,0.3)"
                                    : "var(--border)"
                                }`,
                                borderRadius: "5px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {agent.lockReason === "used" ? "Used" : "Locked"}
                            </span>
                          )}
                          {agent.beta && (
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: "800",
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: "#f59e0b",
                                padding: "2px 7px",
                                background: "rgba(245,158,11,0.1)",
                                border: "1px solid rgba(245,158,11,0.3)",
                                borderRadius: "5px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Beta
                            </span>
                          )}
                          {agentCategories(agent).map((cat) => (
                            <span
                              className={
                                cat === selectedCategory
                                  ? "wrapper__category flt__selected"
                                  : "wrapper__category"
                              }
                              key={cat}
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>

                      <h3 className="agent__name fb__16 fw__700 fc__0E1218 mb__4">
                        {agent.name}
                      </h3>
                      <p className="agent__desc fb__13 fc__5F636A">
                        {agent.description}
                      </p>

                      <div className="wrapper__ag__footer d-flex align-items-center justify-content-between">
                        <span className="badge__category ff__jetBrainsMono fb__12 fc__5F636A d-flex align-items-center">
                          <i className="badge__icon">
                            <img src="/svg/time_icon.svg" alt="Agent Time" />
                          </i>
                          {agent.estimatedTime}
                        </span>
                        <button
                          className="agent__launch__btn fb__14 fw__600 fc__E57600"
                          disabled={agent.beta || agent.locked}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (agent.beta || agent.locked) return;
                            launchAgent(agent.id);
                          }}
                        >
                          {agent.locked ? "Locked" : "Launch"}
                          {!agent.locked && (
                            <span className="btn__arrow">
                              <img
                                src="/svg/lunch_icon.svg"
                                alt="Agent Launch"
                              />
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        )}

        {/* Settings Tab */}
        {/* {showSettingsTab && <SettingsPage />} */}

        {/* History Tab */}
        {/* {showHistoryTab && (
                    <ExecutionHistory
                        key={refreshHistory}
                        agents={agents}
                        onOpenExecution={handleOpenExecution}
                        projects={projects}
                    />
                )} */}
      </div>
    </section>
  );
}
